import React, { useEffect, useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import BreadCrumb from "../../Shared/Components/BreadCrumb";
import ShippingInformation from "./ShippingInformation";
import PaymentInformation from "./PaymentInformation";
import ReviewPurchase from "./ReviewPurchase";
import {
  getStoreId,
  getCatalogId,
  getLangId,
  isBuyOnBehalf
} from "../../Shared/AppContext/selectors";
import { checkHttpStatus, getCurrentFile } from "../../Shared/utility";
import { UserContext } from "../../UserContext";
import parse from "html-react-parser";
import { RotatingLines } from "react-loader-spinner";

const Checkout = () => {
  const storeId = useSelector(getStoreId);
  const catalogId = useSelector(getCatalogId);
  const langId = useSelector(getLangId);
  const {
    orderId,
    cartItemsData: cartItems,
    cartItemsQty,
    selectedLanguage = "",
  } = useContext(UserContext);

  var currentFileRequired = getCurrentFile(selectedLanguage);
  const { t: translationKey } = useTranslation();

  const [creditCardsList, setCreditCardsList] = useState([]);
  const [billMyAccountData, setBillMyAccountData] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [achData, setAchData] = useState([]);
  const [whoPlaceOrder, setWhoPlaceOrder] = useState({ wponame: "" });
  const [primaryContactAddress, setPrimaryContactAddress] = useState({});
  const [selectedAddress, setSelectedAddress] = useState({});
  const [poNumber, setPoNumber] = useState({});
  const [textareaValue, setTextareaValue] = useState("");
  const [emails, setEmails] = useState([]);
  const [emailInputValue, setEmailInputValue] = useState("");
  const [cardSequence, setCardSequence] = useState("");
  const [currentTab, setCurrentTab] = useState("");
  const [addNewCartData, setAddNewCartData] = useState("");
  const [addNewACHAccountData, setAddNewACHAccountData] = useState("");
  const [cardBrandName, setCardBrandName] = useState("");
  const [addAchSeq, setAddAchSeq] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [errorMsgPlaceOrder, setErrorMsgPlaceOrder] = useState("");
  const [noAnyEmail, setNoAnyEmail] = useState(true);
  const [paymentMethodRules, setPaymentMethodRules] = useState([]);
  const [isMainLoading, setIsMainLoading] = useState(true);
  const [bobEmailAddress, setBobEmailAddress] = useState("");
  const [includeDefaultConfirmationEmail, setIncludeDefaultConfirmationEmail] =
    useState(true);

  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const isRedirectingRef = React.useRef(false);

  const isBOB = useSelector(isBuyOnBehalf);

  const token = document.getElementById("reactWCToken")?.value;
  const [totalCost, setTotalCost] = useState(null);
  const currency = cartItems?.currency?.trim()?.toUpperCase() || "USD";
  // Get currency from order API (dynamic)
  const checkoutCurrency =
    cartItems?.grandTotalCurrency ||
    cartItems?.totalProductPriceCurrency ||
    cartItems?.currency ||
    "USD";
  
  // Create currency formatter
  const currencyFormatter = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: checkoutCurrency,
  });

  useEffect(() => {
    if (cartItems) {
      setTotalCost(parseFloat(cartItems?.totalProductPrice).toFixed(2));
    }
  }, [cartItems]);

  useEffect(() => {
    async function getCreditCardDetails() {
      setIsLoadingData(true);
      const response = await fetch(
        `/wcs/resources/store/${storeId}/checkout/creditcards`
      );
      const creditCardsInfo = await checkHttpStatus(response);
      setIsLoadingData(false);
      if (creditCardsInfo) {
        setCreditCardsList(creditCardsInfo.creditCardList);
      }
    }
    async function getBillMyAccountDetails() {
      const response = await fetch(
        `/wcs/resources/store/${storeId}/checkout/billMyAccount`
      );
      const billMyAccountInfo = await checkHttpStatus(response);
      if (billMyAccountInfo) {
        setBillMyAccountData(billMyAccountInfo.billMyAccountList);
      }
    }
    async function getACHAccountDetails() {
      const response = await fetch(
        `/wcs/resources/store/${storeId}/checkout/achDetails`
      );
      const achAccountDetails = await checkHttpStatus(response);
      if (achAccountDetails) {
        setAchData(achAccountDetails.achAccountList);
      }
    }
    async function getPaymentMethodRules() {
      const response = await fetch(
        `/wcs/resources/store/${storeId}/stripe/getPaymentMethodRules`
      );
      const paymentMethodRulesInfo = await checkHttpStatus(response);
      if (paymentMethodRulesInfo) {
        setPaymentMethodRules(paymentMethodRulesInfo.listPaymentMethodRules);
      }
      setIsMainLoading(false);
    }
    if (storeId) {
      getCreditCardDetails();
      getBillMyAccountDetails();
      getACHAccountDetails();
      getPaymentMethodRules();
    }
  }, [storeId]);

  const creditCardRules = React.useMemo(() => {
    if (
      paymentMethodRules.length &&
      paymentMethodRules.findIndex(
        (info) => info.paymentMethodName === "CC"
      ) !== -1
    ) {
      return paymentMethodRules.find((info) => info.paymentMethodName === "CC");
    }
    return null;
  }, [paymentMethodRules]);

  const achRules = React.useMemo(() => {
    if (
      paymentMethodRules.length &&
      paymentMethodRules.findIndex(
        (info) => info.paymentMethodName === "ACH"
      ) !== -1
    ) {
      return paymentMethodRules.find(
        (info) => info.paymentMethodName === "ACH"
      );
    }
    return null;
  }, [paymentMethodRules]);


  const handleFormSubmit = (e) => {
    e.preventDefault();

    if (isPlacingOrder) return;

    setErrorMsg("");
    setErrorMsgPlaceOrder("");

    if (!primaryContactAddress?.customerEmail) {
      if (emails.length === 0) {
        setNoAnyEmail(false);
        setErrorMsgPlaceOrder(
          translationKey(currentFileRequired["EMAIL_VALIDATION_MESSAGE"])
        );
      } else {
        setNoAnyEmail(true);
      }
    }

    const primaryConfirmationEmail = isBOB
      ? includeDefaultConfirmationEmail
        ? bobEmailAddress
        : ""
      : primaryContactAddress?.customerEmail;

    const hasEmail =
      (primaryConfirmationEmail &&
        String(primaryConfirmationEmail).trim().length > 0) ||
      emails.length;

    const hasPayment =
      currentTab === "bill-my-account"
        ? true
        : currentTab === "card"
          ? !!(cardSequence || addNewCartData)
          : !!(addAchSeq || addNewACHAccountData);

    if (!hasEmail || !hasPayment) {
      if (!hasPayment) {
        setErrorMsg(
          translationKey(currentFileRequired["PAYMENT_VALIDATION_MESSAGE"])
        );
      }
      return;
    }

    // Generate idempotency key ONCE for this attempt
    const key =
      window.crypto?.randomUUID?.() ??
      `${Date.now()}-${Math.random().toString(16).slice(2)}`;

    isRedirectingRef.current = false;
    setIsPlacingOrder(true);
    submitHandler(e, key);

  };

  const submitHandler = async (e, idempotencyKey) => {
    e.preventDefault();
    setErrorMsg("");
    setErrorMsgPlaceOrder("");

    const checkoutform = document.getElementById("checkoutform");

    try {
      const data = Array.from(e.target.elements)
        .filter((input) => input.name)
        .reduce(
          (obj, input) => Object.assign(obj, { [input.name]: input.value }),
          {}
        );

      const placeOrderResponse = await fetch(
        `/wcs/resources/store/${storeId}/checkout/submitOrder?langId=${langId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            WCAuthToken: token
              ? token
              : "331732%2CijyhtpJWZ1xAZ9EMQdfKatm6NsUwAPPj3RHpMmyupTc%3D",
            "X-Idempotency-Key": idempotencyKey,
          },
          body: JSON.stringify(data),
        }
      );

      const placeOrderData = await checkHttpStatus(placeOrderResponse);

      if (
        placeOrderData &&
        Object.keys(placeOrderData).length &&
        placeOrderData.paymentStatus === "Success"
      ) {
        Object.keys(placeOrderData).forEach((key) => {
          const input = document.createElement("input");
          input.setAttribute("type", "hidden");
          input.setAttribute("name", key);
          input.setAttribute("value", placeOrderData[key]);
          checkoutform.appendChild(input);
        });

        if (addNewACHAccountData?.statusPaymentIntent === "requires_action") {
          const inputStatusPaymentIntent = document.createElement("input");
          inputStatusPaymentIntent.setAttribute("type", "hidden");
          inputStatusPaymentIntent.setAttribute("name", "achWarningMessage");
          inputStatusPaymentIntent.setAttribute("value", "offline-ACH");
          checkoutform.appendChild(inputStatusPaymentIntent);
        }

        isRedirectingRef.current = true;
        checkoutform.submit();
        return;

      }

      // Business error (including duplicate-submit 409)
      if (placeOrderData?.errors?.length) {

        console.warn("[checkout] submit business error", {
          idempotencyKey,
          httpStatus: placeOrderResponse.status,
          errorMessage: placeOrderData.errors[0]?.errorMessage,
        });

        setErrorMsgPlaceOrder(parse(placeOrderData.errors[0].errorMessage));

      } else {

        console.warn("[checkout] submit unknown business error", {
          idempotencyKey,
          httpStatus: placeOrderResponse.status,
          placeOrderData,
        });

        setErrorMsgPlaceOrder("Something went wrong!!");
      }
    } catch (err) {
      console.error("[checkout] submit exception", { idempotencyKey, err });
      setErrorMsgPlaceOrder("Network error. Please try again.");
      isRedirectingRef.current = false;
    } finally {
      if (!isRedirectingRef.current) {
        setIsPlacingOrder(false);
      }
    }
  };

  return (
    <>
      {isMainLoading ? (
        <div className="loader-wrapper">
          <div className="loader-inner">
            <RotatingLines
              strokeColor="#5A5A5A"
              strokeWidth="5"
              animationDuration="0.75"
              width="45"
              visible={true}
            />
            <p className="loader-text">{translationKey(currentFileRequired["PREPARING_YOUR_ORDER"])}</p>
          </div>
        </div>
      ) : (
        <>
          <BreadCrumb
            currentPageLabel={translationKey(currentFileRequired["CHECKOUT"])}
            currentFileRequired={currentFileRequired}
            translationKey={translationKey}
            showHome
          />
          <div className="checkout-card-header">
            <h1 className="checkout-header-title">
              {translationKey(currentFileRequired["CHECKOUT"])}
            </h1>
          </div>
          <div className="checkout-wrapper">
            <div className="checkout-inner">
              <ShippingInformation
                whoPlaceOrder={whoPlaceOrder}
                setWhoPlaceOrder={setWhoPlaceOrder}
                primaryContactAddress={primaryContactAddress}
                setPrimaryContactAddress={setPrimaryContactAddress}
                selectedAddress={selectedAddress}
                setSelectedAddress={setSelectedAddress}
                emails={emails}
                setEmails={setEmails}
                emailInputValue={emailInputValue}
                setEmailInputValue={setEmailInputValue}
                noAnyEmail={noAnyEmail}
                setNoAnyEmail={setNoAnyEmail}
                setErrorMsgPlaceOrder={setErrorMsgPlaceOrder}
                bobEmailAddress={bobEmailAddress}
                setBobEmailAddress={setBobEmailAddress}
                includeDefaultConfirmationEmail={includeDefaultConfirmationEmail}
                setIncludeDefaultConfirmationEmail={
                  setIncludeDefaultConfirmationEmail
                }
                currentFileRequired={currentFileRequired}
                translationKey={translationKey}
              />

              {!isLoadingData && (
                <PaymentInformation
                  creditCardsList={creditCardsList}
                  billMyAccountData={billMyAccountData}
                  achAccountList={achData}
                  setCreditCardsList={setCreditCardsList}
                  setAchData={setAchData}
                  cardSequence={cardSequence}
                  setCardSequence={setCardSequence}
                  currentTab={currentTab}
                  setCurrentTab={setCurrentTab}
                  addNewCartData={addNewCartData}
                  setAddNewCartData={setAddNewCartData}
                  addNewACHAccountData={addNewACHAccountData}
                  setAddNewACHAccountData={setAddNewACHAccountData}
                  cardBrandName={cardBrandName}
                  setCardBrandName={setCardBrandName}
                  setAddAchSeq={setAddAchSeq}
                  errorMsg={errorMsg}
                  setErrorMsg={setErrorMsg}
                  errorMsgPlaceOrder={errorMsgPlaceOrder}
                  setErrorMsgPlaceOrder={setErrorMsgPlaceOrder}
                  creditCardRules={creditCardRules}
                  achRules={achRules}
                  currentFileRequired={currentFileRequired}
                  translationKey={translationKey}
                />
              )}

              <ReviewPurchase
                poNumber={poNumber}
                setPoNumber={setPoNumber}
                textareaValue={textareaValue}
                setTextareaValue={setTextareaValue}
                currentFileRequired={currentFileRequired}
                translationKey={translationKey}
              />
            </div>
            <div className="checkout-container">
              <div className="estimated-cost-section">
                {translationKey(currentFileRequired["ESTIMATED_SUBTOTAL"])}{" "}
                <span id="item-count">
                  ({cartItemsQty} {translationKey(currentFileRequired["ITEMS"])}
                  )
                </span>
              </div>
              <span className="total-cost"> {currencyFormatter.format(totalCost)}</span>
              <p className="tax-message">
                {translationKey(currentFileRequired["SHIPPING_MESSAGE"])}
              </p>
              <form
                name="checkoutform"
                method="post"
                action="OrderOKView"
                id="checkoutform"
                onSubmit={handleFormSubmit}
              >
                <input
                  type="hidden"
                  name="customerName"
                  value={whoPlaceOrder?.wponame}
                />
                <input
                  type="hidden"
                  name="customername"
                  value={selectedAddress?.customerName}
                />
                <input
                  type="hidden"
                  name="address1"
                  value={selectedAddress?.customerAddress1}
                />
                <input
                  type="hidden"
                  name="address2"
                  value={selectedAddress?.customerAddress2}
                />
                <input
                  type="hidden"
                  name="address3"
                  value={selectedAddress?.customerAddress3}
                />
                <input
                  type="hidden"
                  name="address4"
                  value={selectedAddress?.customerAddress4}
                />
                <input
                  type="hidden"
                  name="state"
                  value={selectedAddress?.customerState}
                />
                <input
                  type="hidden"
                  name="city"
                  value={selectedAddress?.customerCity}
                />
                <input
                  type="hidden"
                  name="ccode"
                  value={selectedAddress?.customerCountryCode}
                />
                <input
                  type="hidden"
                  name="postalcode"
                  value={selectedAddress?.customerZipcode}
                />
                <input
                  type="hidden"
                  name="X_emailAddressTmp"
                  value={
                    isBOB
                      ? includeDefaultConfirmationEmail
                        ? bobEmailAddress
                        : ""
                      : primaryContactAddress?.customerEmail
                  }
                />
                <input
                  type="hidden"
                  name="X_emailAdditional"
                  value={emails?.join(" | ")}
                />
                <input
                  type="hidden"
                  name="ponumber"
                  value={poNumber?.poenter}
                />
                <input
                  type="hidden"
                  name="comments"
                  value={textareaValue?.comment}
                />
                <input type="hidden" name="orderId" value={orderId} />
                <input type="hidden" name="authToken" value={token} />
                <input type="hidden" name="catalogId" value={catalogId} />
                <input type="hidden" name="langId" value={langId} />
                <input type="hidden" name="storeId" value={storeId} />
                <input
                  type="hidden"
                  name="X_ccSeqNbr"
                  value={
                    cardBrandName && cardSequence
                      ? `${cardBrandName}_${cardSequence}`
                      : ""
                  }
                />
                <input type="hidden" name="X_achSeqNbr" value={addAchSeq} />

                <input
                  type="hidden"
                  name="X_useOnFile"
                  value={
                    currentTab == "card" &&
                    !addNewCartData?.X_approvedNewCreditcard
                      ? "Y"
                      : "N"
                  }
                />
                <input
                  type="hidden"
                  name="X_achOnFile"
                  value={
                    currentTab == "ach" &&
                    !addNewACHAccountData?.X_approvedNewACHAccount
                      ? "Y"
                      : "N"
                  }
                />
                <input
                  type="hidden"
                  name="X_approvedNewACHAccount"
                  value={
                    currentTab == "ach" &&
                    addNewACHAccountData?.X_approvedNewACHAccount
                      ? "Y"
                      : "N"
                  }
                />
                <input
                  type="hidden"
                  name="X_billMyAccount"
                  value={currentTab == "bill-my-account" ? "Y" : "N"}
                />
                <input type="hidden" name="policyId" value="10104" />
                <input type="hidden" name="payMethodId" value="10104" />
                <input
                  type="hidden"
                  name="X_approvedNewCreditcard"
                  value={addNewCartData?.X_approvedNewCreditcard ? "Y" : "N"}
                />
                <input
                  type="hidden"
                  name="stripeCustomer"
                  value={addNewCartData?.stripeCustomerId}
                />
                <input
                  type="hidden"
                  name="stripeCardExpiryMonth"
                  value={addNewCartData?.stripeCardExpiryMonth}
                />
                <input
                  type="hidden"
                  name="stripeCardExpiryYear"
                  value={addNewCartData?.stripeCardExpiryYear}
                />
                <input
                  type="hidden"
                  name="stripeCardNum"
                  value={addNewCartData?.cardNumber}
                />
                <input
                  type="hidden"
                  name="stripeCard"
                  value={addNewCartData?.cardBrand}
                />
                <input
                  type="hidden"
                  name="stripeCardId"
                  value={addNewCartData?.stripeCardId}
                />
                <input
                  type="hidden"
                  name="saveForFuture"
                  value={addNewCartData?.saveForFuture}
                />
                {/**ACH DETAILS */}
                <input
                  type="hidden"
                  name="achStripeCustomer"
                  id="achStripeCustomer"
                  value={addNewACHAccountData?.stripeCustomerId}
                />
                <input
                  type="hidden"
                  name="achAccountNumber"
                  id="achAccountNumber"
                  value={addNewACHAccountData?.accountNumber}
                />
                <input
                  type="hidden"
                  name="achBankName"
                  id="achBankName"
                  value={addNewACHAccountData?.bankName}
                />
                <input
                  type="hidden"
                  name="achAccountType"
                  id="achAccountType"
                  value={addNewACHAccountData?.accountType}
                />
                <input
                  type="hidden"
                  name="achPayMethodId"
                  id="achPayMethodId"
                  value={addNewACHAccountData?.paymentMethodId}
                />
                <input
                  type="hidden"
                  name="achSaveForFuture"
                  id="achSaveForFuture"
                  value={addNewACHAccountData?.saveForFuture}
                />
                <button className="proceed-to-checkout-btn" type="submit" disabled={isPlacingOrder}>
                  {isPlacingOrder ? "Processing..." : translationKey(currentFileRequired["PLACE_ORDER"])}
                </button>
              </form>
              {errorMsgPlaceOrder && (
                <p
                  className="checkout-error-message"
                  style={{
                    color: "red",
                    marginTop: 0,
                    fontFamily: "var(--sv-font-sans)",
                  }}
                >
                  {errorMsgPlaceOrder}
                </p>
              )}
              {errorMsg && (
                <p
                  className="checkout-error-message"
                  style={{
                    color: "red",
                    marginTop: 0,
                    fontFamily: "var(--sv-font-sans)",
                  }}
                >
                  {errorMsg}
                </p>
              )}
              <p className="net-price">
                {translationKey(currentFileRequired["NET_PRICE_DESCRIPTION"])}
              </p>
            </div>
          </div>
        </>
      )}
    </>
  );
};
export default Checkout;
